import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency, DIAL_CODES } from "@/context/CurrencyContext";

export const AuthDialog = () => {
  const { authOpen, setAuthOpen, onAuthed } = useAuth();
  const { country, currency } = useCurrency();
  const [mode, setMode] = useState("login");
  const [dial, setDial] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [adult, setAdult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const code = dial || DIAL_CODES[country] || "+27";

  useEffect(() => {
    if (authOpen) {
      setMode("login");
      setError("");
      setName("");
      setPassword("");
      setAdult(false);
    }
  }, [authOpen]);

  const fullPhone = () => {
    const p = phone.replace(/[\s\-()]/g, "");
    if (p.startsWith("+")) return p;
    return code + p.replace(/^0+/, "");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = mode === "login"
        ? { phone: fullPhone(), password }
        : { phone: fullPhone(), password, name, currency, country, is_adult: adult };
      const { data } = await api.post(`/auth/${mode}`, body);
      onAuthed(data);
      toast.success(mode === "login" ? `Welcome back, ${data.user.name}` : "Account created — welcome!");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent data-testid="auth-dialog" className="border-[#1E293B] bg-[#0E131F] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black uppercase tracking-tight">
            {mode === "login" ? "Log in" : "Join betterPlay"}
          </DialogTitle>
        </DialogHeader>
        <Tabs value={mode} onValueChange={(v) => { setMode(v); setError(""); }}>
          <TabsList className="grid w-full grid-cols-2 bg-[#121824]">
            <TabsTrigger value="login" data-testid="auth-tab-login">Log in</TabsTrigger>
            <TabsTrigger value="register" data-testid="auth-tab-register">Register</TabsTrigger>
          </TabsList>
        </Tabs>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <Input data-testid="auth-name-input" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} required />
          )}
          <div className="flex gap-2">
            <select
              data-testid="auth-dial-code-select"
              value={code}
              onChange={(e) => setDial(e.target.value)}
              className="h-9 rounded-md border border-[#1E293B] bg-[#121824] px-2 text-sm"
            >
              {[...new Set(Object.values(DIAL_CODES))].map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <Input
              data-testid="auth-phone-input"
              type="tel"
              placeholder="Cell number e.g. 0821234567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>
          <Input
            data-testid="auth-password-input"
            type="password"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {mode === "register" && (
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <Checkbox data-testid="auth-adult-checkbox" checked={adult} onCheckedChange={(v) => setAdult(!!v)} />
              I confirm I am 18 years or older and accept the terms.
            </label>
          )}
          {error && <p data-testid="auth-error" className="text-sm text-[#EF4444]">{error}</p>}
          <Button data-testid="auth-submit-button" disabled={busy} className="w-full bg-[#10B981] font-bold text-[#0B0E14] hover:bg-[#059669]">
            {busy ? "Please wait…" : mode === "login" ? "Log in" : "Create account"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
